// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  combineReducers,
  configureStore,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import {
  type EqualityFn,
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistStore,
} from 'redux-persist';
import { assetsTypeSlice } from '@shared/data-access/slices';
import availableParameters from './slices/available-parameters';
import broker from './slices/broker';
import { clientsSlice } from '@shared/data-access/slices/clients';
import { authSlice } from '@shared/data-access/slices/auth';
import { userSlice } from '@shared/data-access/slices/user';
import { assetsRepositorySlice } from '@shared/data-access/slices/assets-repository';
import { advisoryRepositorySlice } from '@shared/data-access/slices/advisory-repository';
import { documentsRepositorySlice } from '@shared/data-access/slices/documents-repository';
import { portfolioPositionsSlice } from '@shared/data-access/slices/portfolio-positions';
import { tagManagerSlice } from '@shared/data-access/slices/tag-manager';
import { tagsManagementSlice } from '@shared/data-access/slices/tags-management';
import { illiquidAssetsSlice } from '@shared/data-access/slices/illiquid-assets';
import copilotBuffer from './slices/copilot-buffer';
import myResearch from './slices/my-research';
import parameters from './slices/parameters';
import trader from './slices/trader';
import workflow from './slices/workflow';
import storage from 'redux-persist/lib/storage';
import { deepEqual as isEqual } from 'fast-equals';
import { environment, isDevelopment, isTest } from '@shared/environment';
import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import {
  brokerageApi,
  brokerageApiV2,
} from '@shared/data-access/api-base/citec-brokerage-api';
import {
  engineApi,
  engineApiV2,
} from '@shared/data-access/api-base/citec-engine-api';
import {
  clientSliceMiddleware,
  apiCacheSyncMiddleware,
  syncInstitutionsMiddleware,
} from '@shared/data-access/middlewares';
import { copilotApi } from '@shared/data-access/api-base/citec-copilot-api';
import { auth0Api } from '@shared/data-access/api-base/citec-auth0-api';
import { traderResetMiddleware } from './middlewares/trader-reset.middleware';

/**
 * Combines all reducers into a single root reducer.
 *
 * @returns The root reducer.
 */
export const appReducer = combineReducers({
  [assetsRepositorySlice.reducerPath]: assetsRepositorySlice.reducer,
  [advisoryRepositorySlice.reducerPath]: advisoryRepositorySlice.reducer,
  [documentsRepositorySlice.reducerPath]: documentsRepositorySlice.reducer,
  [assetsTypeSlice.reducerPath]: assetsTypeSlice.reducer,
  availableParameters,
  broker,
  [clientsSlice.reducerPath]: persistReducer(
    {
      whitelist: ['client'],
      key: 'clients',
      storage,
      version: environment.APP.BUILD_ID,
    },
    clientsSlice.reducer,
  ),
  copilotBuffer,
  myResearch,
  parameters,
  [portfolioPositionsSlice.reducerPath]: portfolioPositionsSlice.reducer,
  [tagManagerSlice.reducerPath]: tagManagerSlice.reducer,
  [tagsManagementSlice.reducerPath]: tagsManagementSlice.reducer,
  [illiquidAssetsSlice.reducerPath]: illiquidAssetsSlice.reducer,
  trader,
  [authSlice.reducerPath]: persistReducer(
    {
      whitelist: ['token', 'refreshToken', 'user', 'expiration_date'],
      key: 'auth',
      storage,
      version: environment.APP.BUILD_ID,
    },
    authSlice.reducer,
  ),
  [userSlice.reducerPath]: persistReducer(
    {
      whitelist: ['dashboard', 'accountCurrency', 'userBase'],
      key: 'user',
      storage,
      version: environment.APP.BUILD_ID,
    },
    userSlice.reducer,
  ),
  workflow,
  [aiApi.reducerPath]: aiApi.reducer,
  [brokerageApi.reducerPath]: brokerageApi.reducer,
  [brokerageApiV2.reducerPath]: brokerageApiV2.reducer,
  [engineApi.reducerPath]: engineApi.reducer,
  [engineApiV2.reducerPath]: engineApiV2.reducer,
  [copilotApi.reducerPath]: copilotApi.reducer,
  [auth0Api.reducerPath]: auth0Api.reducer,
});

const rootReducer = (state: any, action: UnknownAction) => {
  if (authSlice.actions.logout.match(action)) {
    return appReducer(undefined, action);
  }

  return appReducer(state, action);
};

/**
 * Configures the persisted reducer with the root reducer.
 *
 * @returns The persisted reducer.
 */
const persistedReducer = persistReducer(
  {
    key: 'root',
    version: environment.APP.BUILD_ID,
    debug: isDevelopment,
    storage,
    blacklist: [
      'errors',
      'staticData',
      'myInvestment',
      'brokers',
      'investments',
      'tickerTags',
      'universe',
      'portfolio',
      'ideas',
      'watchlist',
      'trades',
      'openTrades',
      'portfolioRebalance',
      'staticData',
      'taxHarvesting',
      'summary',
      'rebalance',
      'auth',
      'user',
      'clients',
      'accounts',
      'assetsRepository',
      'advisoryRepository',
      // APIs should not be persisted
      aiApi.reducerPath,
      brokerageApi.reducerPath,
      brokerageApiV2.reducerPath,
      engineApi.reducerPath,
      engineApiV2.reducerPath,
      copilotApi.reducerPath,
      auth0Api.reducerPath,
    ],
  },
  rootReducer,
);

/**
 * Sets up the Redux store with persistence and optional listeners.
 *
 * @param setupStoreListeners - Enables refetchOnFocus and refetchOnReconnect
 *   behaviors.
 * @returns The Redux store and persistor.
 */
export const setupStore = (setupStoreListeners = false, preloadState = {}) => {
  const store = configureStore({
    devTools: {
      maxAge: 50,
      trace: true,
      name: `${environment.APP.NAME}-${environment.NODE_ENV} v${environment.APP.BUILD_ID}`,
    },
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: true,
        immutableCheck: true,
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat([
        aiApi.middleware,
        brokerageApi.middleware,
        brokerageApiV2.middleware,
        engineApi.middleware,
        engineApiV2.middleware,
        copilotApi.middleware,
        auth0Api.middleware,
        apiCacheSyncMiddleware.middleware,
        clientSliceMiddleware.middleware,
        traderResetMiddleware.middleware,
        syncInstitutionsMiddleware.middleware,
      ]),
    preloadedState: preloadState as any,
  });

  /* istanbul ignore if */
  if (setupStoreListeners) {
    setupListeners(store.dispatch);
  }

  const persistor = persistStore(store);

  /* istanbul ignore if */
  if (!isTest) {
    store.subscribe(() => {
      const state = store.getState();

      /* istanbul ignore if */
      if (state._persist.version !== environment.APP.BUILD_ID) {
        persistor.purge();
      }
    });
  }

  return [store, persistor] as const;
};

export const [appStore, persistor] = setupStore();

export type AppStore = ReturnType<typeof setupStore>[0];

export type AppState = ReturnType<typeof appReducer>;

export type AppDispatch = AppStore['dispatch'];

/**
 * Custom hook to use the Redux store's dispatch function.
 *
 * @returns The dispatch function.
 */
export const useAppDispatch = (): AppDispatch => useDispatch();

/**
 * Custom hook to use the Redux store's state.
 *
 * @param selector - The selector function to extract data from the state.
 * @param equalityFn - The function to determine if the selected data has changed.
 * @returns The selected data from the state.
 */
/* istanbul ignore next */
export const useAppSelector: TypedUseSelectorHook<AppState> = (
  selector,
  equalityFn = isEqual,
) => useSelector(selector, equalityFn as EqualityFn<any>);
